import React, { useMemo, useState } from 'react';
import {
  useArticleCollections,
  useCreateArticleCollection,
  useUpdateArticleCollection,
  useReorderArticleCollections,
  useDeleteArticleCollection,
} from '../hooks/useArticleCollections';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/utils';
import { AlertCircle, ArrowDown, ArrowUp, BookOpen, Edit, Library, ListOrdered, Loader2, Plus, Trash2 } from 'lucide-react';
import { ArrangeCollectionArticlesDialog } from './knowledge-base/ArrangeCollectionArticlesDialog';
import { CollectionIcon } from './CollectionIcon';
import { COLLECTION_ICONS } from '../lib/collectionIcons';
import { TreeSelect } from './tree-select/TreeSelect';
import { collectionToOption } from './tree-select/treeOptions';
import type { ArticleCollectionResponse, UpdateArticleCollectionRequest } from '../types/articleCollections';

interface CollectionSettingsProps {
  organizationId: string;
}

interface CollectionFormData {
  name: string;
  description: string;
  icon: string | null;
  parentCollectionId: string | null;
}

const EMPTY_FORM: CollectionFormData = { name: '', description: '', icon: null, parentCollectionId: null };

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * Org Settings → Collections: how the knowledge base is organized for readers - collections, each with one level of
 * sections. Separate from ticket categories. Deleting a collection moves its articles somewhere else.
 */
export const CollectionSettings: React.FC<CollectionSettingsProps> = ({ organizationId }) => {
  const { data: collections = [], isLoading, error, refetch, isFetching } = useArticleCollections(organizationId);
  const createMutation = useCreateArticleCollection(organizationId);
  const updateMutation = useUpdateArticleCollection(organizationId);
  const reorderMutation = useReorderArticleCollections(organizationId);
  const deleteMutation = useDeleteArticleCollection(organizationId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ArticleCollectionResponse | null>(null);
  const [formData, setFormData] = useState<CollectionFormData>(EMPTY_FORM);
  const [nameError, setNameError] = useState<string | null>(null);
  // The collection being deleted, read from the list so a refetch updates its counts in the open dialog
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deleting = deletingId ? collections.find((c) => c.id === deletingId) ?? null : null;
  const [moveTarget, setMoveTarget] = useState<string | null>(null);
  // The collection or section whose articles are being arranged
  const [arranging, setArranging] = useState<ArticleCollectionResponse | null>(null);

  const topLevel = useMemo(() => collections.filter((c) => !c.parentCollectionId), [collections]);
  const sectionsOf = (id: string) => collections.filter((c) => c.parentCollectionId === id);

  // A section can only sit directly under a top-level collection, and a collection with sections stays at the top
  const parentOptions = useMemo(
    () =>
      topLevel
        .filter((c) => c.id !== editing?.id)
        .map((c) => ({ ...collectionToOption(c), disabled: !!editing && editing.sectionCount > 0 })),
    [topLevel, editing]
  );

  const openCreate = (parent?: ArticleCollectionResponse) => {
    setEditing(null);
    setNameError(null);
    setFormData({ ...EMPTY_FORM, parentCollectionId: parent?.id ?? null });
    setDialogOpen(true);
  };

  const openEdit = (collection: ArticleCollectionResponse) => {
    setEditing(collection);
    setNameError(null);
    setFormData({
      name: collection.name,
      description: collection.description ?? '',
      icon: collection.icon ?? null,
      parentCollectionId: collection.parentCollectionId ?? null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = formData.name.trim();
    if (!name) return setNameError('Collection name is required');
    if (name.length > 100) return setNameError('Collection name must be 100 characters or less');
    if (name.includes('›')) return setNameError('Collection names can\'t contain "›"');

    const description = formData.description.trim();
    try {
      if (editing) {
        const updates: UpdateArticleCollectionRequest = {};
        if (name !== editing.name) updates.name = name;
        if (description !== (editing.description ?? '')) updates.description = description || null;
        if (formData.icon !== (editing.icon ?? null)) updates.icon = formData.icon;
        if (formData.parentCollectionId !== (editing.parentCollectionId ?? null)) updates.parentCollectionId = formData.parentCollectionId;
        await updateMutation.mutateAsync({ id: editing.id, updates });
        toast({ title: 'Success', description: 'Collection updated successfully.' });
      } else {
        await createMutation.mutateAsync({
          name,
          description: description || undefined,
          icon: formData.icon ?? undefined,
          parentCollectionId: formData.parentCollectionId ?? undefined,
        });
        toast({ title: 'Success', description: formData.parentCollectionId ? 'Section created successfully.' : 'Collection created successfully.' });
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (err) {
      toast({ title: 'Error', description: getErrorMessage(err, 'Failed to save collection.'), variant: 'destructive' });
    }
  };

  const handleMove = async (collection: ArticleCollectionResponse, direction: -1 | 1) => {
    const siblings = collection.parentCollectionId ? sectionsOf(collection.parentCollectionId) : topLevel;
    const index = siblings.findIndex((c) => c.id === collection.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= siblings.length) return;

    const orderedIds = siblings.map((c) => c.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target], orderedIds[index]];
    try {
      await reorderMutation.mutateAsync({ parentCollectionId: collection.parentCollectionId ?? null, orderedIds });
    } catch (err) {
      toast({ title: 'Error', description: getErrorMessage(err, 'Failed to reorder collections.'), variant: 'destructive' });
    }
  };

  const openDelete = (collection: ArticleCollectionResponse) => {
    setDeletingId(collection.id);
    // A section's articles go back to its collection by default; a collection's are left uncategorized
    setMoveTarget(collection.parentCollectionId ?? null);
    // The counts decide whether to ask where the articles go - articles filed since the list loaded must count
    void refetch();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync({ id: deleting.id, moveArticlesToCollectionId: moveTarget });
      toast({ title: 'Success', description: `"${deleting.path}" deleted.` });
      setDeletingId(null);
    } catch (err) {
      toast({ title: 'Error', description: getErrorMessage(err, 'Failed to delete collection.'), variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading collections...
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">{getErrorMessage(error, 'Failed to load collections. Please try again.')}</p>
        </CardContent>
      </Card>
    );
  }

  const deletingArticles = deleting ? deleting.articleCount + deleting.archivedArticleCount : 0;
  const saving = createMutation.isPending || updateMutation.isPending;

  const renderRow = (collection: ArticleCollectionResponse) => {
    const isSection = !!collection.parentCollectionId;
    const siblings = isSection ? sectionsOf(collection.parentCollectionId!) : topLevel;
    const index = siblings.findIndex((c) => c.id === collection.id);
    return (
      <li
        key={collection.id}
        className={cn('flex flex-col sm:flex-row sm:items-center gap-2 py-2.5 px-1 sm:px-2', isSection && 'sm:pl-10 pl-6')}
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={cn('flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0', isSection ? 'bg-muted' : 'bg-primary/10 text-primary')}>
            {isSection ? <BookOpen className="h-4 w-4 text-muted-foreground" /> : <CollectionIcon icon={collection.icon} className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{collection.name}</span>
              {isSection && <Badge variant="outline" className="text-xs font-normal">Section</Badge>}
            </div>
            {collection.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{collection.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-2 pl-11 sm:pl-0">
          <div className="flex items-center gap-1.5">
            <Badge variant="secondary" className="text-xs whitespace-nowrap" title={`${collection.publishedArticleCount} published`}>
              {plural(collection.articleCount, 'article', 'articles')}
            </Badge>
            {collection.sectionCount > 0 && (
              <Badge variant="outline" className="text-xs whitespace-nowrap">{plural(collection.sectionCount, 'section', 'sections')}</Badge>
            )}
          </div>
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleMove(collection, -1)}
              disabled={reorderMutation.isPending || index <= 0} title="Move up" aria-label={`Move ${collection.name} up`}>
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleMove(collection, 1)}
              disabled={reorderMutation.isPending || index >= siblings.length - 1} title="Move down" aria-label={`Move ${collection.name} down`}>
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openCreate(collection)}
              disabled={isSection} title={isSection ? 'Sections can\'t have sections' : 'Add section'} aria-label={`Add a section to ${collection.name}`}>
              <Plus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setArranging(collection)}
              disabled={collection.articleCount === 0} title="Arrange articles" aria-label={`Arrange the articles in ${collection.name}`}>
              <ListOrdered className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(collection)} title="Edit" aria-label={`Edit ${collection.name}`}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openDelete(collection)} title="Delete" aria-label={`Delete ${collection.name}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold">Knowledge Base Collections</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            How customers browse your help center: collections, each with optional sections. The customer portal
            shows the ones that have published articles, in this order.
          </p>
        </div>
        <Button onClick={() => openCreate()} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Add Collection
        </Button>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Library className="h-4 w-4 sm:h-5 sm:w-5" />
            Collections ({topLevel.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-4 pt-0 sm:pt-0">
          {topLevel.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Library className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No collections yet</p>
              <p className="text-sm">Create one to organize your knowledge base articles</p>
            </div>
          ) : (
            <ul className="divide-y">
              {topLevel.map((collection) => (
                <React.Fragment key={collection.id}>
                  {renderRow(collection)}
                  {sectionsOf(collection.id).map(renderRow)}
                </React.Fragment>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {arranging && (
        <ArrangeCollectionArticlesDialog
          open
          onOpenChange={(open) => !open && setArranging(null)}
          organizationId={organizationId}
          collectionId={arranging.id}
          collectionName={arranging.path}
        />
      )}

      {/* Create / edit */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? (editing.parentCollectionId ? 'Edit Section' : 'Edit Collection') : formData.parentCollectionId ? 'New Section' : 'New Collection'}
            </DialogTitle>
            <DialogDescription>Collections and sections are what customers browse in the help center.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="collection-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="collection-name"
                value={formData.name}
                onChange={(e) => { setFormData((p) => ({ ...p, name: e.target.value })); setNameError(null); }}
                placeholder="e.g. Getting Started"
                className={nameError ? 'border-red-500' : ''}
              />
              {nameError && (
                <p className="flex items-center gap-1 text-sm text-red-500"><AlertCircle className="h-4 w-4" />{nameError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="collection-parent">Inside collection</Label>
              <TreeSelect
                id="collection-parent"
                options={parentOptions}
                value={formData.parentCollectionId}
                onChange={(id) => setFormData((p) => ({ ...p, parentCollectionId: id }))}
                noneLabel="None (a top-level collection)"
                placeholder="None (a top-level collection)"
                searchPlaceholder="Search collections..."
                disabled={!!editing && editing.sectionCount > 0}
              />
              <p className="text-xs text-muted-foreground">
                {editing && editing.sectionCount > 0
                  ? 'A collection with sections stays at the top level.'
                  : 'Choose a collection to make this a section inside it.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="collection-description">Description</Label>
              <Textarea
                id="collection-description"
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional. Shown on the collection's card in the help center."
                rows={2}
                maxLength={500}
              />
            </div>
            {!formData.parentCollectionId && (
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="grid grid-cols-8 gap-1.5" role="radiogroup" aria-label="Collection icon">
                  {Object.entries(COLLECTION_ICONS).map(([name, { icon: Icon, label }]) => (
                    <button
                      key={name}
                      type="button"
                      role="radio"
                      aria-checked={formData.icon === name}
                      aria-label={label}
                      title={label}
                      onClick={() => setFormData((p) => ({ ...p, icon: p.icon === name ? null : name }))}
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
                        formData.icon === name ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent text-muted-foreground'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={!formData.name.trim() || saving}>
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete: choose where the articles go */}
      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeletingId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete "{deleting?.path}"</DialogTitle>
            <DialogDescription>
              {deleting && deleting.sectionCount > 0
                ? `It has ${plural(deleting.sectionCount, 'section', 'sections')}. Move or delete them first.`
                : deletingArticles > 0
                  ? `${plural(deletingArticles, 'article is', 'articles are')} filed here. Choose where ${deletingArticles === 1 ? 'it goes' : 'they go'}; nothing else about ${deletingArticles === 1 ? 'it' : 'them'} changes.`
                  : 'Nothing is filed here.'}
            </DialogDescription>
          </DialogHeader>
          {deleting && deleting.sectionCount === 0 && deletingArticles > 0 && (
            <div className="space-y-2">
              <Label htmlFor="collection-move-target">Move articles to</Label>
              <TreeSelect
                id="collection-move-target"
                options={collections.filter((c) => c.id !== deleting.id).map(collectionToOption)}
                value={moveTarget}
                onChange={setMoveTarget}
                noneLabel="Uncategorized"
                placeholder="Uncategorized"
                searchPlaceholder="Search collections..."
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              // Not until the counts are fresh: they decide whether the articles' destination was asked for
              disabled={deleteMutation.isPending || isFetching || (!!deleting && deleting.sectionCount > 0)}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
